import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { API_BASE_URL } from '../../config';
import './AdminAICosts.css';

interface UserCost {
  cost: number;
  requests: number;
  input_tokens: number;
  cached_input_tokens: number;
  output_tokens: number;
}

interface DailyEntry {
  date: string;
  total_cost: number;
  total_requests: number;
  users: Record<number, UserCost>;
}

interface UserTotal {
  cost: number;
  requests: number;
  name: string;
}

interface CostsData {
  daily: DailyEntry[];
  user_totals: Record<string, UserTotal>;
  days: number;
}

const COLORS = [
  '#4fc3f7', '#ef5350', '#66bb6a', '#ffa726', '#ab47bc',
  '#26c6da', '#ff7043', '#8d6e63', '#78909c', '#ec407a',
];

const AdminAICosts: React.FC = () => {
  const [data, setData] = useState<CostsData | null>(null);
  const [days, setDays] = useState(30);
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/admin/ai-costs/daily?days=${days}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days]);

  if (loading) return <div className="card">Loading AI costs...</div>;
  if (!data) return <div className="card">Failed to load data</div>;

  const sortedUsers = Object.entries(data.user_totals)
    .sort(([, a], [, b]) => b.cost - a.cost);

  const totalCost = sortedUsers.reduce((s, [, u]) => s + u.cost, 0);
  const totalRequests = sortedUsers.reduce((s, [, u]) => s + u.requests, 0);

  // Build chart data
  const chartData = data.daily.map(day => {
    const point: Record<string, any> = {
      date: day.date.slice(5), // MM-DD
      total: day.total_cost,
    };
    selectedUsers.forEach(uid => {
      const userCost = day.users[uid];
      point[`user_${uid}`] = userCost ? userCost.cost : 0;
    });
    return point;
  });

  const toggleUser = (uid: number) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  return (
    <div className="admin-ai-costs">
      <div className="ai-costs-header">
        <h2>AI Costs — ${totalCost.toFixed(2)} total, {totalRequests} requests</h2>
        <div className="ai-costs-period">
          {[7, 30, 90, 365].map(d => (
            <button
              key={d}
              className={`period-btn ${days === d ? 'active' : ''}`}
              onClick={() => setDays(d)}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="ai-costs-chart">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey="date" fontSize={11} tick={{ fill: '#666' }} />
            <YAxis fontSize={11} tick={{ fill: '#666' }} tickFormatter={v => `$${v}`} />
            <Tooltip
              formatter={(value: any, name: any) => {
                const label = name === 'total' ? 'Total' : data.user_totals[String(name).replace('user_', '')]?.name || name;
                return [`$${Number(value).toFixed(4)}`, label];
              }}
            />
            <Legend
              formatter={(value: string) => {
                if (value === 'total') return 'Total';
                return data.user_totals[value.replace('user_', '')]?.name || value;
              }}
            />
            <Line
              type="monotone"
              dataKey="total"
              stroke="#333"
              strokeWidth={2}
              dot={false}
            />
            {Array.from(selectedUsers).map((uid, i) => (
              <Line
                key={uid}
                type="monotone"
                dataKey={`user_${uid}`}
                stroke={COLORS[i % COLORS.length]}
                strokeWidth={1.5}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="ai-costs-users">
        <table>
          <thead>
            <tr>
              <th></th>
              <th>User</th>
              <th>Cost</th>
              <th>Requests</th>
              <th>Avg/req</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map(([uid, user], i) => (
              <tr
                key={uid}
                className={selectedUsers.has(Number(uid)) ? 'selected' : ''}
                onClick={() => toggleUser(Number(uid))}
              >
                <td>
                  <span
                    className="user-color-dot"
                    style={{
                      backgroundColor: selectedUsers.has(Number(uid))
                        ? COLORS[Array.from(selectedUsers).indexOf(Number(uid)) % COLORS.length]
                        : '#ccc'
                    }}
                  />
                </td>
                <td>{user.name}</td>
                <td className="num">${user.cost.toFixed(2)}</td>
                <td className="num">{user.requests}</td>
                <td className="num">${user.requests > 0 ? (user.cost / user.requests).toFixed(4) : '0'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAICosts;
